/* eslint-disable max-classes-per-file */
/* eslint-disable class-methods-use-this */
import React from 'react'
import { Trans } from 'react-i18next/icu.macro'
import { i18n } from '@fs/zion-locale'
import { Link } from '@fs/zion-router'
import { invalidValidationString } from '@fs/zion-phone-number'
import { invalidCharactersString, tokenInvalidString } from '@fs/identity.js'
import { fieldNames, signOutLink } from './constants'
import {
  churchRecordNumberFieldString,
  emailFieldString,
  firstNamesFieldString,
  invalidFieldString,
  lastNamesFieldString,
  usernameFieldString,
} from './commonStrings'

export class ValidationError extends Error {
  constructor(fieldName, ...params) {
    super(...params)
    this.fieldName = fieldName
  }

  render() {
    return this.message
  }
}

// Extend this class for validation errors when you want Sentry reporting
// on how often it is thrown (would be for edge cases for errors).
// Actually reporting this type of error is handled in throwCisError.
export class InvalidFieldError extends ValidationError {}

// utility function to help prevent swallowing edge case validation errors
// where backend returns validation errors for fields not in the form
export const canDisplayAsFormError = (error, formFieldNames) =>
  error instanceof ValidationError && formFieldNames.includes(error.fieldName)

const helpCenterLink = '/help/helpcenter/article/how-do-i-add-my-membership-record-number-mrn'

// has to have children so it will work with i18n locales sync script
// use wayfinding otherwise it may be the same color as the validation error messages
const FindMyAccountLink = ({ children }) => (
  <Link external to="/recover/find-my-account" wayfinding>
    {children}
  </Link>
)

export class AccountAlreadyExistsError extends ValidationError {
  constructor() {
    super(fieldNames.email, 'A user already exists with the First Name, Last Name, and E-mail you provided.')
  }

  render() {
    return (
      <Trans i18nKey="email-name-match-account.validation">
        The email and name you entered match an existing account. Try using a different email or name. You can check for
        a duplicate account <FindMyAccountLink>here</FindMyAccountLink>.
      </Trans>
    )
  }
}

export class AccountAlreadyExistsSmsError extends ValidationError {
  constructor() {
    super(fieldNames.smsNumber, 'A user already exists with the First Name, Last Name, and Mobile Number you provided.')
  }

  render() {
    return (
      <Trans i18nKey="mobile-phone-name-match-account.validation">
        The mobile number and name you entered match an existing account. Try using a different mobile number or name.
        You can check for a duplicate account <FindMyAccountLink>here</FindMyAccountLink>.
      </Trans>
    )
  }
}

export class ChurchRecordNumberAlreadyInUseError extends ValidationError {
  constructor() {
    super(fieldNames.churchRecordNumber, 'It looks like you already have a member account.')
  }

  render() {
    // use wayfinding otherwise it may be the same color as the validation error messages
    return (
      <Trans i18nKey="already-in-use-church-record-number.validation">
        It looks like you already have a member account. Exit this account and try to{' '}
        <Link external to={signOutLink} wayfinding>
          sign in
        </Link>{' '}
        to your member account. If you don’t have the username or password we can help you recover them. You can also
        visit our{' '}
        <Link external to={helpCenterLink} wayfinding>
          Help Center
        </Link>{' '}
        for more options.
      </Trans>
    )
  }
}

export class ChurchRecordNumberInvalidError extends ValidationError {
  constructor() {
    super(
      fieldNames.churchRecordNumber,
      i18n.t('invalid-church-record-number.validation', 'Either your Church Record Number or birth date are incorrect.')
    )
  }
}

export class ChurchRecordNumberParentInvalidError extends ValidationError {
  constructor() {
    super(fieldNames.churchRecordNumber, invalidFieldString(churchRecordNumberFieldString()))
  }
}

export class ContactIdAlreadyInUseError extends ValidationError {
  constructor() {
    super(fieldNames.contactId, i18n.t('already-in-use-contact-id.validation', 'Contact ID already taken'))
  }
}

export class InvalidCharacterContactIdError extends ValidationError {
  constructor() {
    super(fieldNames.contactId, invalidCharactersString())
  }
}

export class EmailMaxAccountsError extends ValidationError {
  constructor() {
    super(
      fieldNames.email,
      i18n.t(
        'email-maximum-accounts.validation',
        'The email is already associated with the maximum number of accounts.'
      )
    )
  }
}

export class InvalidCharacterEmailError extends ValidationError {
  constructor() {
    super(fieldNames.email, invalidCharactersString())
  }
}

export class InvalidCharacterFirstNamesError extends ValidationError {
  constructor() {
    super(fieldNames.firstNames, invalidCharactersString())
  }
}

export class InvalidCharacterLastNamesError extends ValidationError {
  constructor() {
    super(fieldNames.lastNames, invalidCharactersString())
  }
}

export class InvalidCharacterUsernameError extends ValidationError {
  constructor() {
    super(fieldNames.username, invalidCharactersString())
  }
}

export class InvalidFirstNamesError extends InvalidFieldError {
  constructor() {
    super(fieldNames.firstNames, invalidFieldString(firstNamesFieldString()))
  }
}

export class InvalidEmailError extends ValidationError {
  constructor() {
    super(fieldNames.email, invalidFieldString(emailFieldString()))
  }
}

export class InvalidEmailReportedError extends InvalidFieldError {
  constructor() {
    super(fieldNames.email, invalidFieldString(emailFieldString()))
  }
}

export class InvalidLastNamesError extends InvalidFieldError {
  constructor() {
    super(fieldNames.lastNames, invalidFieldString(lastNamesFieldString()))
  }
}

// The more specific MobileNumberInvalidError should be used for specific validation
// cases we expect the backend to catch
export class InvalidSmsNumberError extends InvalidFieldError {
  constructor() {
    super(fieldNames.smsNumber, invalidValidationString())
  }
}

export class InvalidUsernameError extends InvalidFieldError {
  constructor() {
    super(fieldNames.username, invalidFieldString(usernameFieldString()))
  }
}

export class MobileNumberAlreadyInUseError extends ValidationError {
  constructor() {
    super(
      fieldNames.smsNumber,
      i18n.t(
        'already-in-use-mobile-phone.validation',
        'The mobile phone number you entered is already used in another account.'
      )
    )
  }
}

// This is distinct from the generic InvalidSmsNumberError as InvalidSmsNumberError
// is an InvalidFieldError that we'd like to monitor so we can improve
// the messaging if needed
export class MobileNumberInvalidError extends ValidationError {
  constructor() {
    super(fieldNames.smsNumber, invalidValidationString())
  }
}

export class ParentalConsentRequiredError extends Error {}

export class PasswordIncorrectError extends ValidationError {
  constructor() {
    super(fieldNames.currentPassword, i18n.t('password-incorrect.validation', 'Incorrect password'))
  }
}

export class UsernameAlreadyInUseError extends ValidationError {
  constructor() {
    super(fieldNames.username, i18n.t('already-in-use-username.validation', 'Username already exists.'))
  }
}

export class TreeStartingPersonIdInvalidError extends ValidationError {
  constructor() {
    super(
      fieldNames.treeStartingPersonId,
      i18n.t('invalid-family-tree-starting-person.validation', 'Invalid ID number')
    )
  }
}

export class VerificationCodeInvalidError extends ValidationError {
  constructor() {
    super(fieldNames.verificationCode, tokenInvalidString())
  }
}

export class VerificationCompleteError extends Error {
  constructor() {
    // once we move off the sync and use a new verification service we will be revisiting this
    super('SMS verification has already been completed or cannot be completed due to a sync issue.')
  }
}
